import { useGetPendingSyncState } from "@shared/generated/crm/queries/syncStateController/useGetPendingSyncState";
import type { PendingSyncStateDto } from "@shared/generated/crm/models/PendingSyncStateDto";

export type CrmSyncState =
  | { status: "disabled" }
  | { status: "loading" }
  | { status: "fetch-error" }
  | { status: "syncing"; entries: PendingSyncStateDto[]; hasErrors: boolean }
  | { status: "idle";    entries: PendingSyncStateDto[]; hasErrors: boolean };

function resourceSyncIsDone(entry: PendingSyncStateDto): boolean {
  return entry.toFetch === "0" && entry.toProcess === "0";
}

function deriveState(entries: PendingSyncStateDto[]): Exclude<CrmSyncState, { status: "loading" }> {
  const isSyncing = entries.some((e) => !resourceSyncIsDone(e));
  const hasErrors = entries.some((e) => e.lastFailure != null);
  return isSyncing
    ? { status: "syncing", entries, hasErrors }
    : { status: "idle", entries, hasErrors };
}

const THREE_MINUTES_MS = 3 * 60 * 1000;

export const useCrmSyncState = ({
  enabled,
}: {
  enabled: boolean;
}): CrmSyncState => {
  const { data, isLoading, isError } = useGetPendingSyncState({
    query: {
      enabled,
      staleTime: THREE_MINUTES_MS,
      refetchInterval: THREE_MINUTES_MS,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      select: deriveState,
    },
  });

  if (!enabled) return { status: "disabled" };
  if (isLoading) return { status: "loading" };
  if (isError) return { status: "fetch-error" };
  return data ?? { status: "idle", entries: [], hasErrors: false };
};
